// /m/:slug — the full §5.4 join flow. Hosts join directly (§6.1); everyone
// else — signed-in or anonymous guest (F-PLAT-4) — knocks and waits for the
// host's decision (F-LOB-2). The admitted WS envelope is the only place the
// room token and E2EE secret arrive.

import type { RoomConnectionState } from '@brizina/call-engine';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router';

import { api, ApiError, JoinGrant, loginUrl, Me, MeetingState, readModelBSecret } from '../api';
import { BrizinaLoader } from '../components/BrizinaLoader';
import { CallView } from '../components/CallView';
import { PreJoin, PreJoinChoices } from '../components/PreJoin';
import { engine } from '../engine';
import { KnockWatch, watchKnock } from '../realtime';
import { useCallStore } from '../store/callStore';
import { connectRoom } from '../lib/connectRoom';
import { humanConnectError } from '../lib/connectError';

type Phase =
  | { kind: 'loading' }
  | { kind: 'state-page'; message: string }
  | { kind: 'prejoin'; state: MeetingState; me: Me | null }
  | { kind: 'knocking'; hostPresent: boolean }
  | { kind: 'denied' }
  | { kind: 'expired' }
  | { kind: 'unsupported'; message: string }
  | { kind: 'in-call' }
  | { kind: 'error'; message: string };

const guestIdKey = (slug: string) => `bz-guest-id:${slug}`;
const GUEST_NAME_KEY = 'bz-guest-name';

export function MeetingPage() {
  const { slug = '' } = useParams();
  const location = useLocation();
  // Set by HomePage when the host just created this instant meeting: skip the
  // lobby (they're alone as host) and show the one-time share card in-call.
  const [justCreated] = useState(
    () => Boolean((location.state as { justCreated?: boolean } | null)?.justCreated),
  );
  const [me, setMe] = useState<Me | null>(null);
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });
  const [joining, setJoining] = useState(false);
  const autoJoinedRef = useRef(false);
  // Stable prop for CallView: only set when the host just created this meeting.
  const readyCard = useMemo(
    () => (justCreated ? { joinedAs: me?.email ?? me?.name ?? '' } : null),
    [justCreated, me],
  );
  // Set when the duration-cap timer fires, so the disconnect screen can show the
  // "time's up" message without reading the clock during render.
  const [hitCap, setHitCap] = useState(false);
  // Meeting title survives phase transitions (shown in the call top bar).
  const [meetingTitle, setMeetingTitle] = useState<string | null>(null);
  const roomState = useCallStore((s) => s.roomState);
  const removedReason = useCallStore((s) => s.removedReason);
  const meetingEndsAt = useCallStore((s) => s.meetingEndsAt);
  const durationCapMin = useCallStore((s) => s.durationCapMin);
  const myRole = useCallStore((s) => s.myRole);
  const knockRef = useRef<KnockWatch | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const state = await api.meetingState(slug);
        if (cancelled) return;
        if (state.state === 'ended') {
          setPhase({ kind: 'state-page', message: 'This meeting has ended.' });
          return;
        }
        let me: Me | null = null;
        try {
          me = await api.me();
        } catch {
          me = null; // anonymous guest — welcome since M2 (F-PLAT-4)
        }
        if (!cancelled) {
          setMe(me);
          setMeetingTitle(state.title);
          setPhase({ kind: 'prejoin', state, me });
        }
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404) {
          setPhase({ kind: 'state-page', message: 'This meeting does not exist.' });
        } else {
          setPhase({
            kind: 'error',
            message: err instanceof Error ? err.message : 'Failed to load',
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const enterCall = useCallback(
    async (grant: JoinGrant, choices: PreJoinChoices) => {
      try {
        await connectRoom(grant, choices); // shared with /call/:id — never drifts
      } catch (err) {
        const message = humanConnectError(err);
        if (message.includes('end-to-end encryption')) {
          setPhase({ kind: 'unsupported', message });
        } else {
          // The raw error still matters — to us. It goes to the console, not
          // the person.
          console.error('room connect failed', err);
          setPhase({ kind: 'error', message });
        }
        return;
      }
      setPhase({ kind: 'in-call' });
    },
    [],
  );

  const join = useCallback(
    (choices: PreJoinChoices) => {
      setJoining(true);
      void (async () => {
        try {
          if (phase.kind === 'prejoin' && phase.me) {
            // Signed-in: try the host's direct path first.
            try {
              const grant = await api.join(slug);
              await enterCall(grant, choices);
              return;
            } catch (err) {
              const knockRequired = err instanceof ApiError && err.status === 403;
              if (!knockRequired) throw err;
            }
          }
          // Knock phase (§5.4): guests remember identity + name per §5.5.
          if (!choices.name) throw new Error('name required');
          window.sessionStorage.setItem(GUEST_NAME_KEY, choices.name);
          const remembered = window.sessionStorage.getItem(guestIdKey(slug)) ?? undefined;
          const created = await api.knock(slug, choices.name, remembered);
          window.sessionStorage.setItem(guestIdKey(slug), created.guestId);

          const watch = watchKnock(created.knockId, (hostPresent) =>
            setPhase({ kind: 'knocking', hostPresent }),
          );
          knockRef.current = watch;
          setPhase({ kind: 'knocking', hostPresent: true });

          const outcome = await watch.outcome;
          knockRef.current = null;
          switch (outcome.kind) {
            case 'admitted':
              await enterCall(outcome.grant, choices);
              break;
            case 'denied':
              setPhase({ kind: 'denied' });
              break;
            case 'expired':
              setPhase({ kind: 'expired' });
              break;
          }
        } catch (err) {
          if (err instanceof ApiError && err.status === 410) {
            setPhase({ kind: 'state-page', message: 'This meeting has ended.' });
          } else if (err instanceof ApiError && err.status === 403) {
            // Surface the server's reason (locked, removed, …) verbatim.
            setPhase({ kind: 'state-page', message: err.message });
          } else {
            setPhase({
              kind: 'error',
              message: err instanceof Error ? err.message : 'Could not join',
            });
          }
        } finally {
          setJoining(false);
        }
      })();
    },
    [slug, phase, enterCall],
  );

  // Instant-create shortcut (host only): skip the lobby. Once the meeting state
  // has loaded (phase 'prejoin' with a signed-in me), join immediately with
  // sensible defaults — reusing the very same join() the lobby button calls, so
  // there is no second join path to keep in sync.
  useEffect(() => {
    if (!justCreated || autoJoinedRef.current) return;
    if (phase.kind !== 'prejoin' || !phase.me) return;
    autoJoinedRef.current = true;
    const choices = { micEnabled: true, camEnabled: true, name: phase.me.name };
    // Defer out of the effect's synchronous pass (same idiom as the load
    // effect); join() then drives the phase transitions itself.
    void Promise.resolve().then(() => join(choices));
  }, [justCreated, phase, join]);

  const cancelKnock = useCallback(() => {
    knockRef.current?.cancel();
    knockRef.current = null;
    window.location.reload();
  }, []);

  const retry = useCallback(() => window.location.reload(), []);
  const goHome = useCallback(() => {
    window.location.href = '/';
  }, []);

  // Leaving the page must always tear the connection down.
  useEffect(
    () => () => {
      knockRef.current?.cancel();
      void engine.disconnect();
    },
    [],
  );

  // Client-side duration cap: leave exactly when the countdown reaches 0, so the
  // meeting ends on time for this participant instead of waiting for the server's
  // periodic force-end sweep (which lags by up to its interval). The server
  // reaper stays the authority that force-ends the room for everyone.
  useEffect(() => {
    if (phase.kind !== 'in-call' || meetingEndsAt <= 0) return;
    const endCall = () => {
      setHitCap(true);
      void engine.disconnect();
    };
    const msLeft = meetingEndsAt * 1000 - Date.now();
    if (msLeft <= 0) {
      endCall();
      return;
    }
    const t = window.setTimeout(endCall, msLeft);
    return () => window.clearTimeout(t);
  }, [phase.kind, meetingEndsAt]);

  // Engine disconnect (leave, kicked, room ended) exits the call view —
  // derived in render, no setState-in-effect cascade.
  if (phase.kind === 'in-call' && (roomState as RoomConnectionState) === 'disconnected') {
    // The org duration cap force-ends the room like a host end-for-all, so tell
    // the two apart by proximity to the known cap time and give the host their
    // own message (Phase C).
    const hitTimeLimit = hitCap;
    const isHost = myRole === 'host' || myRole === 'cohost';
    let message: string;
    let sub: string | null = null;
    if (hitTimeLimit) {
      const cap = durationCapMin > 0 ? `${durationCapMin}-minute ` : '';
      message = "Time's up — the meeting has ended.";
      sub = isHost
        ? `Your workspace's ${cap}meeting limit was reached, so the meeting ended for everyone.`
        : `This meeting reached its ${cap}time limit.`;
    } else {
      message = removedReason ?? 'You left the meeting.';
    }
    return (
      <div className="bz-state-page">
        <p className="bz-state-title">{message}</p>
        {sub && <p className="bz-state-sub">{sub}</p>}
        <a className="bz-btn bz-btn-primary" href="/">
          Back to home
        </a>
      </div>
    );
  }

  switch (phase.kind) {
    case 'loading':
      return <div className="bz-state-page">Loading…</div>;
    case 'state-page':
      return (
        <div className="bz-state-page">
          <p>{phase.message}</p>
          <a className="bz-btn bz-btn-primary" href="/">
            Back to home
          </a>
        </div>
      );
    case 'prejoin': {
      // Instant-create host skips the lobby: show a brief connecting state while
      // the auto-join effect takes them straight in.
      if (justCreated && phase.me) {
        return (
          <div className="bz-state-page">
            <BrizinaLoader />
            <p>Starting your meeting…</p>
          </div>
        );
      }
      const isGuest = phase.me === null;
      const rememberedName = isGuest
        ? (window.sessionStorage.getItem(GUEST_NAME_KEY) ?? '')
        : (phase.me?.name ?? '');
      // Model B needs the passphrase in the lobby unless the link carries it.
      const needPassphrase = phase.state.e2eeModel === 'b' && readModelBSecret() === null;
      return (
        <>
          <PreJoin
            meetingTitle={phase.state.title}
            slug={slug}
            initialName={rememberedName}
            nameRequired
            nameEditable={isGuest}
            needPassphrase={needPassphrase}
            joining={joining}
            joinLabel={isGuest ? 'Ask to join' : 'Join now'}
            attendees={phase.state.attendees}
            attendeeCount={phase.state.attendeeCount}
            onJoin={join}
            onCancel={goHome}
          />
          {isGuest && (
            <p className="bz-prejoin-signin-hint">
              Organizing meetings needs an account — <a href={loginUrl}>sign in</a>. Joining this
              one doesn&rsquo;t.
            </p>
          )}
        </>
      );
    }
    case 'knocking':
      return (
        <div className="bz-state-page">
          <BrizinaLoader />
          <p>
            {phase.hostPresent
              ? 'Waiting for the host to let you in…'
              : 'Waiting for the host to start the meeting…'}
          </p>
          <button className="bz-btn" onClick={cancelKnock}>
            Cancel
          </button>
        </div>
      );
    case 'denied':
      return (
        <div className="bz-state-page">
          <p>You weren&rsquo;t admitted to this meeting.</p>
          <a className="bz-btn" href="/">
            Back to home
          </a>
        </div>
      );
    case 'expired':
      return (
        <div className="bz-state-page">
          <p>No answer from the host.</p>
          <button className="bz-btn bz-btn-primary" onClick={retry}>
            Try again
          </button>
        </div>
      );
    case 'unsupported':
      return (
        <div className="bz-state-page">
          <p>{phase.message}</p>
          <p className="bz-prejoin-denied">
            This meeting is end-to-end encrypted; joining without encryption is not possible.
          </p>
        </div>
      );
    case 'in-call':
      return <CallView slug={slug} title={meetingTitle} readyCard={readyCard} />;
    case 'error':
      return (
        <div className="bz-state-page">
          <p>{phase.message}</p>
          <button className="bz-btn bz-btn-primary" onClick={retry}>
            Try again
          </button>
        </div>
      );
  }
}
